/**
 * Das Kundenfenster (Bühne) als benanntes Popup — eine Referenz für die ganze
 * Sitzung.
 *
 * Warum ein Modul und keine lokale `useRef`: Das Cockpit ist der Besitzer der
 * Präsentation, aber es wird zwischen Review, Cockpit und Ende neu gerendert.
 * Eine Referenz in einem Komponentenzustand ginge dabei verloren, und ein
 * zweiter `window.open`-Aufruf mit demselben Namen holte zwar dasselbe Fenster
 * nach vorn, lüde es aber neu — mitten im Termin. Deshalb lebt die Referenz
 * hier, überlebt jedes Rerender und wird beim zweiten Aufruf nur fokussiert.
 *
 * Der Fenstername `msc-cv-stage` ist zusätzlich der Rückfall des Browsers:
 * Selbst nach einem Cockpit-Reload landet die Bühne wieder im selben Fenster.
 *
 * Die Bühne bekommt den `owner` (die `ownerId` der Präsentation) in der
 * Adresse mit und hört am Kanal nur auf dieses Cockpit (`presentChannel.ts`).
 * Ein offenes Fenster einer ANDEREN Präsentation wird deshalb nicht nur nach
 * vorn geholt, sondern mit der neuen Adresse neu geladen.
 */

const STAGE_PATH = "/customer-view/stage";
const STAGE_NAME = "msc-cv-stage";
const STAGE_FEATURES = "popup,width=1280,height=800";

let stageWindow: Window | null = null;
/** Owner, mit dem das offene Fenster geladen wurde. */
let openedFor: string | null = null;

function stageUrl(owner: string): string {
  return owner === "" ? STAGE_PATH : `${STAGE_PATH}?owner=${encodeURIComponent(owner)}`;
}

/** Offen? (`closed` ist auch für ein vom Kunden geschlossenes Fenster wahr.) */
export function isStageWindowOpen(): boolean {
  return stageWindow !== null && !stageWindow.closed;
}

/**
 * Öffnet die Bühne für die Präsentation `owner` oder holt das bereits offene
 * Fenster nach vorn — sofern es zu DERSELBEN Präsentation gehört; sonst wird
 * das benannte Fenster mit der neuen Adresse neu geladen. `null`, wenn der
 * Browser das Popup blockiert hat — der Aufrufer sagt es dem Lead.
 */
export function openStageWindow(owner: string): Window | null {
  if (isStageWindowOpen() && openedFor === owner) {
    stageWindow!.focus();
    return stageWindow;
  }
  stageWindow = window.open(stageUrl(owner), STAGE_NAME, STAGE_FEATURES);
  openedFor = stageWindow === null ? null : owner;
  return stageWindow;
}

/** Schließt die Bühne, falls sie noch offen ist, und vergisst die Referenz. */
export function closeStageWindow(): void {
  if (isStageWindowOpen()) stageWindow!.close();
  stageWindow = null;
  openedFor = null;
}
